import {
  APIDashboardFilterExpression,
  ArdoqId,
  DashboardFilterValues,
} from '@ardoq/api-types';
import {
  actionCreator,
  persistentReducedStream,
  reducer,
} from '@ardoq/rxbeach';
import { isEmpty, omit } from 'lodash';
import { loadPresentation } from 'presentation/actions';

/**
 * Filter values the viewer picked while paging through a presentation, keyed by
 * dashboard rather than by slide: filter parameter ids are only unique within a
 * dashboard, and `loadedDashboards$` caches by dashboard too, so slides sharing
 * a dashboard keep one selection instead of invalidating each other's data.
 */
export type PresentationDashboardFiltersState = Record<
  ArdoqId,
  DashboardFilterValues
>;

const defaultState: PresentationDashboardFiltersState = {};

export type PresentationDashboardFilterChangedPayload = {
  dashboardId: ArdoqId;
  dashboardFilterId: string;
  values: APIDashboardFilterExpression[] | null;
};

export const presentationDashboardFilterChanged =
  actionCreator<PresentationDashboardFilterChangedPayload>(
    '[presentationDashboardFilters] FILTER_CHANGED'
  );

export const presentationDashboardFiltersCleared = actionCreator<{
  dashboardId: ArdoqId;
}>('[presentationDashboardFilters] FILTERS_CLEARED');

export const setFilterValues = (
  state: PresentationDashboardFiltersState,
  {
    dashboardId,
    dashboardFilterId,
    values,
  }: PresentationDashboardFilterChangedPayload
): PresentationDashboardFiltersState => {
  const currentValues = state[dashboardId] ?? {};
  const updatedValues =
    !values || isEmpty(values)
      ? omit(currentValues, dashboardFilterId)
      : { ...currentValues, [dashboardFilterId]: values };

  return { ...state, [dashboardId]: updatedValues };
};

export const clearFilterValues = (
  state: PresentationDashboardFiltersState,
  { dashboardId }: { dashboardId: ArdoqId }
): PresentationDashboardFiltersState => omit(state, dashboardId);

const resetFilterValues = (): PresentationDashboardFiltersState => defaultState;

export const presentationDashboardFilters$ =
  persistentReducedStream<PresentationDashboardFiltersState>(
    'presentationDashboardFilters$',
    defaultState,
    [
      reducer(presentationDashboardFilterChanged, setFilterValues),
      reducer(presentationDashboardFiltersCleared, clearFilterValues),
      reducer(loadPresentation, resetFilterValues),
    ]
  );
